import * as styles from "./MonthPickerHeader.css";

import { clsx } from "clsx";
import type { JSX } from "react";
import { useMemo } from "react";

import { Icon } from "@frontend/components/atoms/Icon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import { useLingui } from "@lingui/react/macro";

import { useMonthPickerContext } from "./MonthPicker.context";
import type { MonthPickerHeaderProps } from "./MonthPicker.types";
import { thisMonth } from "./monthMath";

export function MonthPickerHeader({
  className,
}: MonthPickerHeaderProps): JSX.Element {
  const { t } = useLingui();

  const { viewYear, min, max, goToYear, goToView } = useMonthPickerContext();

  const yearOptions = useMemo(() => {
    const minYear = min?.year ?? thisMonth().year - 100;
    const maxYear = max?.year ?? thisMonth().year + 10;

    return Array.from({ length: maxYear - minYear + 1 }, (_, index) => {
      const year = minYear + index;

      return { value: String(year), label: String(year) };
    });
  }, [min, max]);

  const atMin = null !== min && viewYear <= min.year;
  const atMax = null !== max && viewYear >= max.year;

  return (
    <div
      data-slot="month-picker-header"
      className={clsx(styles.header, className)}
    >
      <button
        type="button"
        className={styles.navButton}
        onClick={() => goToYear(-1)}
        disabled={atMin}
        aria-label={t`Previous year`}
      >
        <Icon name="ChevronLeft" size="sm" />
      </button>

      <Select
        size="sm"
        items={yearOptions}
        value={String(viewYear)}
        onValueChange={(value) => {
          if (value) {
            goToView(Number(value));
          }
        }}
      >
        <SelectTrigger className={styles.yearSelectTrigger}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {yearOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <button
        type="button"
        className={styles.navButton}
        onClick={() => goToYear(1)}
        disabled={atMax}
        aria-label={t`Next year`}
      >
        <Icon name="ChevronRight" size="sm" />
      </button>
    </div>
  );
}
